import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type {
  ListingsAPIAndFilter,
  ListingsAPIOrFilter,
} from '../../common/types/ListingsAPIFilter';
import type { ListingsAPIFacetSpec } from '../types/ListingsAPIFacetSpec';
import type {
  ListingsAPIDeliveryZone,
  ListingsAPIRequestBody,
} from '../types/ListingsAPIRequestBody';

export class ListingsRequestBuilder {
  private request: ListingsAPIRequestBody = {
    currencyCode: 'GBP',
    deliveryZone: 'MAINLAND_UK',
    facetSpecs: [],
    filter: {},
  };

  currency(code: CurrencyCode): this {
    this.request.currencyCode = code;
    return this;
  }

  /**
   * Defaults to `MAINLAND_UK`, mirroring how `currency` defaults to GBP. The
   * default is a convenience for the majority case and not a safety net: a caller
   * serving a non-UK customer that forgets this quotes them UK delivery, so set it
   * from the customer's zone rather than relying on the default.
   */
  deliveryZone(zone: ListingsAPIDeliveryZone): this {
    this.request.deliveryZone = zone;
    return this;
  }

  offset(offset: number): this {
    this.request.offset = offset;
    return this;
  }

  pageSize(size: number): this {
    this.request.pageSize = size;
    return this;
  }

  searchTerm(term: string): this {
    this.request.searchTerm = term;
    return this;
  }

  facetSpecs(facetSpecs: ListingsAPIFacetSpec[]): this {
    if (this.request.facetSpecs.length) {
      throw new Error('facet specs already set on the request');
    }

    this.request.facetSpecs = facetSpecs;
    return this;
  }

  filter(filter: ListingsAPIAndFilter | ListingsAPIOrFilter): this {
    if (this.request.filter && Object.keys(this.request.filter).length > 0) {
      throw new Error('a filter is already set on the request');
    }
    this.request.filter = filter;
    return this;
  }

  pageCategories(pageCategories: string[]): this {
    this.request.pageCategories = pageCategories;
    return this;
  }

  sortBy(key: string, descending: boolean): this {
    this.request.sortBy = key ? `${key}${descending ? ' desc' : ''}` : '';
    return this;
  }

  build(): ListingsAPIRequestBody {
    return { ...this.request };
  }
}
